import * as React from 'react';
import { useEffect, useState } from 'react';
import { IntegrationModel } from '@journeyapps-platform/lib-journey-core';
import { useSystem } from '../../../../hooks/useSystem';
import { SelectWidget } from '../../../../widgets/select/SelectWidget';
import { styled } from '../../../../theme';
import { getErrorData } from '@journeyapps-platform/micro-errors';

export interface InstallAzureIntegrationDialogWidgetProps {
  integration: IntegrationModel;
  setActionHandler: (handler: { handler: () => Promise<boolean> }) => any;
  setValid: (valid: boolean) => any;
}

export interface OrgSelectProps extends InstallAzureIntegrationDialogWidgetProps {
  selected: (org: string) => any;
}

const OrgSelect: React.FC<OrgSelectProps> = (props) => {
  const system = useSystem();
  const [orgs, setOrgs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      const client = system.clientProvisioner.provisionAzureIntegrationClient();
      const orgs = await client.listOrganizations({
        integration_id: props.integration.model.id
      });
      setOrgs(orgs);
      setLoading(false);
    })();
  }, []);

  return (
    <S.SelectContainer>
      <S.SelectLabel>{`Azure DevOps Organization`}</S.SelectLabel>
      <SelectWidget
        onChange={(v: any) => {
          props.selected(v.value);
        }}
        isLoading={loading}
        defaultValue={null}
        placeholder="Select organization"
        options={orgs.map((o) => {
          return {
            value: o,
            label: o
          };
        })}
      />
    </S.SelectContainer>
  );
};

export interface ProjectSelectProps extends InstallAzureIntegrationDialogWidgetProps {
  selected: (project: string) => any;
  azureOrg: string;
}

const ProjectSelect: React.FC<ProjectSelectProps> = (props) => {
  const system = useSystem();
  const [projects, setProjects] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      const client = system.clientProvisioner.provisionAzureIntegrationClient();
      const projects = await client.listProjects({
        integration_id: props.integration.model.id,
        organization: props.azureOrg
      });
      setProjects(projects);
      setLoading(false);
    })();
  }, [props.azureOrg]);

  return (
    <S.SelectContainer>
      <S.SelectLabel>{`Azure DevOps Project`}</S.SelectLabel>
      <SelectWidget
        onChange={(v: any) => {
          props.selected(v.value);
        }}
        isLoading={loading}
        defaultValue={null}
        placeholder="Select Azure DevOps Project"
        options={projects.map((o) => {
          return {
            value: o,
            label: o
          };
        })}
      />
    </S.SelectContainer>
  );
};

export const InstallAzureIntegrationDialogWidget: React.FC<InstallAzureIntegrationDialogWidgetProps> = (props) => {
  const system = useSystem();
  const [azureOrg, setAzureOrg] = useState<string>();
  const [azureProject, setAzureProject] = useState<string | null>();
  useEffect(() => {
    props.setValid(!!azureProject);
    props.setActionHandler({
      handler: async () => {
        const client = system.clientProvisioner.provisionAzureIntegrationClient();

        try {
          await client.finalizeIntegration({
            integration_id: props.integration.model.id,
            organization: azureOrg!,
            project: azureProject!
          });
          return true;
        } catch (ex) {
          const jerror = getErrorData(ex);
          system.dialogStore.errorDialog({
            title: 'Installation error',
            errorMessage: jerror
              ? jerror.description
              : 'Oh no, something went wrong and this Azure DevOps integration could not complete installation.'
          });
        }
        return false;
      }
    });
  }, [azureProject]);
  return (
    <S.Container>
      <OrgSelect
        {...props}
        selected={(org) => {
          setAzureProject(null);
          setAzureOrg(org);
        }}
      />
      {azureOrg ? (
        <ProjectSelect
          {...props}
          azureOrg={azureOrg}
          selected={(project) => {
            setAzureProject(project);
          }}
        />
      ) : null}
    </S.Container>
  );
};
namespace S {
  export const SelectContainer = styled.div<{ padTop?: boolean }>`
    width: 100%;
    color: ${(p) => p.theme.text.primary};
    margin-bottom: 16px;

    &:last-of-type {
      margin-bottom: 0;
    }
  `;

  export const SelectLabel = styled.div`
    color: ${(p) => p.theme.text.secondary};
    padding-bottom: 8px;
  `;

  export const Container = styled.div``;
}
